import type { CardProps, TableColumnsType } from "antd";
import { Table } from "antd";
import i18n from "i18next";
import type { FC } from "react";

import { DashboardCardContainer } from "src/app/features/Dashboard/utils/dashboardStyles";
import { NotificationType } from "../../../../components/NotificationAlerts";
import { NotificationAlert } from "../../../../components/NotificationAlerts/NotificationAlert";
import {
    displayCampaignUpdateStatusValue,
    displayDateTimeValue,
} from "../../../../components/Table/components/PropertyTable/transformations/cellTransformations";
import { TableCursorStyle } from "../../../../components/Table/styles/TableCursorStyle";
import type { RecentCampaignDto } from "../types/Dashboard";
import { formatDateTime } from "../utils/formatters";

interface RecentCampaignsCardProps extends CardProps {
    campaigns: RecentCampaignDto[];
    loading?: boolean;
    error?: unknown;
}

/**
 * RecentCampaignsCard - Table-based list of recent campaigns
 */
export const RecentCampaignsCard: FC<RecentCampaignsCardProps> = ({ campaigns, loading = false, error, ...cardProps }) => {
    const columns: TableColumnsType<RecentCampaignDto> = [
        {
            title: i18n.t("campaignCreate.terminal.confVersion"),
            dataIndex: "confVersion",
            key: "confVersion",
        },
        {
            title: i18n.t("campaignCreate.terminal.name"),
            dataIndex: "name",
            key: "name",
            ellipsis: true,
        },
        {
            title: i18n.t("dashboard.recentCampaigns.validity"),
            key: "validity",
            render: (_, record) => {
                if (record.validFrom && record.validTo) {
                    return `${formatDateTime(record.validFrom)} - ${formatDateTime(record.validTo)}`;
                }
                if (record.validFrom) {
                    return `From ${formatDateTime(record.validFrom)}`;
                }
                if (record.validTo) {
                    return `Until ${formatDateTime(record.validTo)}`;
                }
                return formatDateTime(record.created);
            },
        },
        {
            title: i18n.t("terminalCreateForm.numberOfTerminals"),
            dataIndex: "terminalCount",
            key: "terminalCount",
            align: "center",
        },
        {
            title: i18n.t("campaignCreate.terminal.status"),
            dataIndex: "state",
            key: "state",
            render: (value) => displayCampaignUpdateStatusValue(value),
        },
        {
            title: i18n.t("campaignList.owner"),
            dataIndex: "owner",
            key: "owner",
        },
        {
            title: i18n.t("enum.updateStatus.created"),
            dataIndex: "created",
            key: "created",
            render: (value) => displayDateTimeValue(value),
        },
    ];

    if (error) {
        return (
            <DashboardCardContainer title={i18n.t("dashboard.recentCampaigns.title")} {...cardProps}>
                <NotificationAlert
                    description={error.toString() || ""}
                    titleKey="dashboard.recentCampaigns.error"
                    type={NotificationType.ERROR}
                    isCloseable={false}
                />
            </DashboardCardContainer>
        );
    }

    return (
        <DashboardCardContainer title={i18n.t("dashboard.recentCampaigns.title")} {...cardProps}>
            <TableCursorStyle />
            <Table
                columns={columns}
                dataSource={campaigns}
                loading={loading}
                rowKey="id"
                pagination={{
                    pageSize: 10,
                    showSizeChanger: false,
                }}
                locale={{
                    emptyText: i18n.t("dashboard.recentCampaigns.empty"),
                }}
                scroll={{ x: true }}
                className="aevi-table"
            />
        </DashboardCardContainer>
    );
};
